import { NamedValueKey, PmcMessageType } from '@reachmedianetwork/post-message-client-rds-api';
import { assert } from '../Utility/assert';
import { PlayerAbstract } from './PlayerAbstract';
import {
    OutboundApi,
    SignsMessenger,
    createSignsPostMessageClient,
} from './SignsPostMessageClient';
import type {
    GetRSFilePromiseSuccess,
    HeartbeatInterface,
    InstalledPackage,
    ListRSFilesPromiseSuccess,
    NtpInterface,
    PlayerControlInterface,
    PlayerPackageInterface,
    RequiredInterfaces,
    ScreenshotInterface,
    ScreenshotPromiseSuccess,
    setTimeZoneResponse,
    SystemUpdateObject,
    TimeZoneInterface,
    VideoAssetCacheInterface,
} from './interfaces';
import { moment } from '../../vendor/moment';
import { getVideoAssetUrl } from './Assets';
import { Heartbeat } from './Heartbeat';

type OptionalPmcInterfaces = ScreenshotInterface &
    PlayerControlInterface &
    PlayerPackageInterface &
    NtpInterface &
    TimeZoneInterface &
    VideoAssetCacheInterface &
    HeartbeatInterface;

export class PlayerPmc extends PlayerAbstract implements RequiredInterfaces, OptionalPmcInterfaces {
    #pmsgr: SignsMessenger | null = null;
    #knownAssets: Map<string, string>;
    readonly #heartbeat: Heartbeat;

    constructor() {
        super();

        this.#knownAssets = new Map();

        this.#heartbeat = new Heartbeat({
            enable: () => this.postMessage(PmcMessageType.HeartbeatEnable, undefined),
            ping: () => this.postMessage(PmcMessageType.HeartbeatPing, undefined),
        });
    }

    async initializePlatform(): Promise<void> {
        await super.initializePlatform();

        if (this.#pmsgr === null) {
            this.#pmsgr = await this.#createSignsPostMessageClient();
        }
        if (this.supportsMessage(PmcMessageType.InitializePlatform)) {
            await this.postMessage(PmcMessageType.InitializePlatform, undefined);
        }
    }

    async #createSignsPostMessageClient(): Promise<SignsMessenger> {
        assert(window.top !== null, 'Cannot access parent window.');
        return createSignsPostMessageClient(window.parent);
    }

    protected async postMessage<T extends Parameters<SignsMessenger['client']['postMessage']>[0]>(
        type: T,
        payload: OutboundApi[T]['requestPayload']
    ): Promise<OutboundApi[T]['responsePayload']> {
        if (this.#pmsgr === null) {
            this.#pmsgr = await this.#createSignsPostMessageClient();
        }
        if (!this.#pmsgr.supports(type)) {
            throw new ReferenceError(`'${type}' not supported.`);
        }
        return this.#pmsgr.client.postMessage(type, payload);
    }

    supportsMessage(msgType: string): boolean {
        assert(this.#pmsgr !== null, 'Messenger not initialized.');
        return this.#pmsgr.supports(msgType);
    }

    async deleteNamedValue(keyName: NamedValueKey): Promise<void> {
        await this.postMessage(PmcMessageType.NamedValueDelete, { key: keyName });
    }

    getNamedValue(keyName: NamedValueKey): Promise<string | null>;
    getNamedValue(keyName: NamedValueKey, defaultValue: string): Promise<string>;
    async getNamedValue(keyName: NamedValueKey, defaultValue?: string): Promise<string | null> {
        const value = await this.postMessage(PmcMessageType.NamedValueGet, {
            key: keyName,
        });
        return value ?? defaultValue ?? null;
    }

    async setNamedValue(keyName: NamedValueKey, keyValue: string | unknown): Promise<void> {
        const value = String(keyValue);
        await this.postMessage(PmcMessageType.NamedValueSet, {
            key: keyName,
            value,
        });
    }

    async getScreenshot(): Promise<ScreenshotPromiseSuccess> {
        const { base64Image, mimeType } = await this.postMessage(
            PmcMessageType.ScreenshotGet,
            undefined
        );
        assert(mimeType === 'image/jpeg', 'Only JPEG images are currently supported.');
        return {
            base64Image,
            mimeType,
        };
    }

    async clearBrowserCacheOnReboot(): Promise<void> {
        await this.postMessage(PmcMessageType.ClearBrowserCacheOnReboot, undefined);
    }
    async rebootSystem(): Promise<void> {
        await this.setNamedValue(
            NamedValueKey.LastRestart,
            String(moment().format('YYYY-MM-DD HH:mm:ss'))
        );
        await this.postMessage(PmcMessageType.RebootSystem, undefined);
    }

    async getInstalledPackages(): Promise<Array<InstalledPackage>> {
        const installed = await this.postMessage(PmcMessageType.PlayerPackagesInstalled, undefined);
        return installed.map((pkg) => {
            return {
                Guid: pkg.guid,
                Name: pkg.name,
                VersionAsString: pkg.version,
                Description: pkg.description,
            };
        });
    }

    async deployPackage(
        notificationChannel: string,
        playerUuid: string,
        guid: string,
        version: string,
        purgeDownloadedPackage: boolean
    ): Promise<void> {
        await this.postMessage(PmcMessageType.PlayerPackagesDeploy, {
            notificationChannel,
            playerUuid,
            guid,
            version,
            purgeDownloadedPackage,
        });
    }

    async enableNtp(server: string, interval: number): Promise<void> {
        await this.postMessage(PmcMessageType.NtpEnable, {
            server,
            interval,
        });
    }
    async disableNtp(): Promise<void> {
        await this.postMessage(PmcMessageType.NtpDisable, undefined);
    }

    async setTimeZone(timeZone: string): Promise<setTimeZoneResponse> {
        const response = await this.postMessage(PmcMessageType.TimeZoneSet, { timeZone });
        return {
            rebootRequired: response.restartRequired,
        };
    }

    async cacheVideoAsset(assetUuid: string): Promise<boolean> {
        let result;

        const response = await this.postMessage(PmcMessageType.AssetStatus, { cacheId: assetUuid });
        switch (response.status) {
            case 'cached':
                result = true;
                // already cached
                break;
            case 'caching':
                result = false;
                // nothing needs doing
                break;
            case 'not-cached': {
                result = false;
                // start caching asset
                const videoUrl = await getVideoAssetUrl(assetUuid); // TODO: pass url via parameter
                await this.postMessage(PmcMessageType.AssetDownload, {
                    url: videoUrl,
                    cacheId: assetUuid,
                });
                this.#knownAssets.set(assetUuid, videoUrl);
                break;
            }
        }

        return result;
    }

    async getVideoPath(assetUuid: string): Promise<string> {
        const response = await this.postMessage(PmcMessageType.AssetStatus, { cacheId: assetUuid });
        if (response.status === 'cached' && response.localUrl !== null) {
            return response.localUrl;
        }

        const videoUrl = this.#knownAssets.get(assetUuid);
        assert(videoUrl !== undefined, `Unknown asset ${assetUuid}`);
        return videoUrl;
    }

    async isCachedAsset(assetUuid: string): Promise<boolean> {
        const response = await this.postMessage(PmcMessageType.AssetStatus, { cacheId: assetUuid });
        return response.status === 'cached';
    }

    async startSendingHeartbeat(): Promise<void> {
        await this.#heartbeat.start();
    }

    async getPlayerVersion(): Promise<string> {
        const response = await this.postMessage(PmcMessageType.PlayerInformationGet, undefined);
        return response.version;
    }
    async reloadSignage(): Promise<void> {
        return this.postMessage(PmcMessageType.ReloadSignage, undefined);
    }
    async getRootStorageFile(fiName: string): Promise<GetRSFilePromiseSuccess> {
        const { fileBase64 } = await this.postMessage(PmcMessageType.RootStorageFileGet, {
            fileName: fiName,
        });
        return { fiString: fileBase64 };
    }
    async getListOfRootStorageFiles(): Promise<ListRSFilesPromiseSuccess> {
        const { files } = await this.postMessage(PmcMessageType.RootStorageFileList, undefined);
        return { diFiles: files };
    }
    schedulePlatformUpdate(systemUpdate: SystemUpdateObject): Promise<void> {
        return this.postMessage(PmcMessageType.SchedulePlatformUpdate, {
            day: systemUpdate.day,
            time: systemUpdate.time,
            enabled: systemUpdate.isEnabled,
        });
    }
}
